import React, { useEffect, useState } from 'react';
import Grid from '@material-ui/core/Grid';
import makeStyles from '@material-ui/styles/makeStyles';
import { MaterialTable } from '../../components';
import api from '../../common/api';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import Divider from '@material-ui/core/Divider';
import useTheme from '@material-ui/styles/useTheme';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import PPMobileCardView from '../../components/PPMobileCardView';

const useStyles = makeStyles(theme => ({
    section: {
        marginBottom: theme.spacing(3)
    }
}));

// Errors that simply mean "this farm has no rows yet" — render an empty
// table instead of an error banner. A real 404 (route missing on the server)
// is NOT treated as empty: it should surface so the misconfig is visible.
const isEmptyDataError = err => {
    const msg = (err && err.message) || '';
    return msg === 'Bad Request' || msg === 'No data found';
};

const buildRows = data =>
    data.map(row => [
        row.materialName,
        row.availableStock != null ? row.availableStock.toFixed(2) : '',
        row.averageCost != null ? row.averageCost.toFixed(2) : ''
    ]);

const AvailableSummary = ({ showError, showMessage, match }) => {
    const classes = useStyles();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'), {
        defaultMatches: true
    });
    const farmID = match.params['farmID'];
    const [loading, setLoading] = useState(false);
    const [medicines, setMedicines] = useState([]);
    const [vaccinations, setVaccinations] = useState([]);
    const [feed, setFeed] = useState([]);

    const load = () => {
        showError('');
        showMessage('');
        setLoading(true);

        const medicineReq = api
            .post(`/medicine-stock/get-medicine-vaccination-list/${farmID}`, {})
            .then(res => {
                setMedicines(Array.isArray(res?.Medicine) ? res.Medicine : []);
                setVaccinations(Array.isArray(res?.Vaccination) ? res.Vaccination : []);
            })
            .catch(err => {
                if (!isEmptyDataError(err)) showError(err.message || 'Unknown Error');
            });

        const feedReq = api
            .post(`/feed-stock/get-feed-list/${farmID}`, {})
            .then(res => {
                setFeed(Array.isArray(res?.Feed) ? res.Feed : []);
            })
            .catch(err => {
                if (!isEmptyDataError(err)) showError(err.message || 'Unknown Error');
            });

        Promise.allSettled([medicineReq, feedReq]).finally(() => setLoading(false));
    };

    useEffect(load, []);

    const handleExportCSV = (data, header, fileName) => {
        const csvContent = [header, ...buildRows(data)].map(e => e.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${fileName}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleExportPDF = (data, header, title) => {
        try {
            const doc = new jsPDF();
            doc.text(title, 14, 15);
            doc.autoTable({
                head: [header],
                body: buildRows(data),
                startY: 20,
                theme: 'grid',
                styles: { fontSize: 10, halign: 'center', cellPadding: 3 },
                headStyles: { fillColor: [60, 66, 77], textColor: 255, fontStyle: 'bold', halign: 'center' },
                bodyStyles: { textColor: 50 }
            });
            doc.save(`${title}.pdf`);
        } catch (err) {
            console.error('PDF Export Failed:', err);
        }
    };

    const renderTable = (title, data, unitLabel) => {
        const header = ['Name', `Available Quantity (${unitLabel})`, `Average Cost per ${unitLabel}`];
        const farmName = window.localStorage.getItem('farmName');
        return (
            <div className={`${classes.section} pp-table-actions-nowrap`}>
                <MaterialTable
                    isLoading={loading}
                    title={title}
                    columns={[
                        {
                            hidden: isDesktop,
                            render: rd => <PPMobileCardView
                                title={rd.materialName}
                                rows={[
                                    { label: `Available Quantity (${unitLabel})`, value: rd.availableStock != null ? rd.availableStock.toFixed(2) : '', bold: true },
                                    { label: `Average Cost per ${unitLabel}`, value: rd.averageCost != null ? rd.averageCost.toFixed(2) : '' },
                                ]}
                            />,
                            // Other columns are hidden on mobile, and material-table skips hidden columns when searching
                            customFilterAndSearch: (term, rd) => {
                                const values = [rd.materialName, rd.availableStock, rd.averageCost];
                                return values.some(value => value !== undefined && value !== null &&
                                    String(value).toLowerCase().includes(term.toLowerCase()));
                            },
                        },
                        { title: 'Name', field: 'materialName', editable: 'never', hidden: !isDesktop },
                        {
                            title: `Available Quantity (${unitLabel})`,
                            field: 'availableStock',
                            editable: 'never',
                            hidden: !isDesktop,
                            render: rd => (rd.availableStock != null ? rd.availableStock.toFixed(2) : '')
                        },
                        {
                            title: `Average Cost per ${unitLabel}`,
                            field: 'averageCost',
                            editable: 'never',
                            hidden: !isDesktop,
                            render: rd => (rd.averageCost != null ? rd.averageCost.toFixed(2) : '')
                        }
                    ]}
                    data={data}
                    options={{
                        actionsColumnIndex: -1,
                        search: true,
                        showTitle: true,
                        toolbar: true,
                        header: isDesktop,
                        // Mobile: narrower search box (material-table's default min width is 150px) so the table title isn't cut off
                        searchFieldStyle: isDesktop ? undefined : { minWidth: 0, width: 110, paddingLeft: 8 },
                        emptyRowsWhenPaging: false
                    }}
                    localization={{ body: { emptyDataSourceMessage: `No ${title.toLowerCase()} available` } }}
                    actions={[
                        {
                            icon: 'save_alt',
                            tooltip: 'Export to Excel',
                            isFreeAction: true,
                            onClick: () => handleExportCSV(data, header, title)
                        },
                        {
                            icon: () => <PictureAsPdfIcon />,
                            tooltip: 'Export to Pdf',
                            isFreeAction: true,
                            onClick: () => handleExportPDF(data, header, `${title} (${farmName})`)
                        }
                    ]}
                />
            </div>
        );
    };

    return (
        <Grid container>
            <Grid item xs={12} md={6}>
                {renderTable('Medicines', medicines, 'units')}
            </Grid>
            {/* Mobile: a divider between the three tables so each list is easy to tell apart */}
            {!isDesktop && <Grid item xs={12}><Divider className="pp-section-divider" /></Grid>}
            <Grid item xs={12} md={6}>
                {renderTable('Vaccinations', vaccinations, 'units')}
            </Grid>
            {!isDesktop && <Grid item xs={12}><Divider className="pp-section-divider" /></Grid>}
            <Grid item xs={12}>
                {renderTable('Feed', feed, 'kg')}
            </Grid>
        </Grid>
    );
};

export default AvailableSummary;
